import type { CodeableConcept, Reference, Resource } from '@medplum/fhirtypes';

import {
  Caption,
  Checkbox,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTooltip,
  Stack,
  Text,
} from '@eluve/components';

import { resourceDisplayName } from '../views/accessors';

/** A closed-value-set entry a ConceptSelectInput can offer. */
export type ConceptOption = {
  code: string;
  system: string;
  display: string;
};

export function conceptFromOption(option: ConceptOption): CodeableConcept {
  return {
    coding: [
      { system: option.system, code: option.code, display: option.display },
    ],
    text: option.display,
  };
}

/**
 * Edit-mode input primitives — the form twins of the datatype displays in
 * displays.tsx. Dumb controlled components: views bind them to
 * useFhirEditState and own the immutable resource updates. Empty input
 * maps to undefined so cleared fields diff to JSONPatch removes.
 */

function FieldShell({
  label,
  required,
  invalid,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  invalid?: boolean;
  /** Optional plain-language explanation, shown behind an (i) tooltip. */
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label className="flex min-w-0 flex-col gap-1">
        <Caption color="tertiary">
          {label}
          {required && (
            <span aria-hidden="true" className="text-destructive">
              {' '}
              *
            </span>
          )}
          {hint && (
            // A button so keyboard/AT users can reach the hint (tooltip opens
            // on focus). preventDefault keeps the click from activating the
            // wrapping label and focusing the input. The tooltip provider
            // lives at the renderer root (FhirRendererProvider).
            <SimpleTooltip label={hint}>
              <button
                type="button"
                aria-label={hint}
                onClick={(clickEvent) => clickEvent.preventDefault()}
                className="ml-1 inline-flex cursor-help align-middle text-text-tertiary"
              >
                <Icon name="Info" size="xs" />
              </button>
            </SimpleTooltip>
          )}
        </Caption>
        <div className="min-w-0 privacy-text">{children}</div>
      </label>
      {invalid && <Caption className="text-destructive">Required</Caption>}
    </div>
  );
}

export function StringInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  hint,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  placeholder?: string;
  type?: 'text' | 'tel' | 'email';
  required?: boolean;
  hint?: string;
}) {
  const invalid = Boolean(required && !value?.trim());
  return (
    <FieldShell label={label} required={required} invalid={invalid} hint={hint}>
      <Input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        onChange={(event) => onChange(event.target.value || undefined)}
      />
    </FieldShell>
  );
}

/** FHIR integer primitive; empty clears the field. */
export function IntegerInput({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <FieldShell label={label} hint={hint}>
      <Input
        type="number"
        min={0}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(event) => {
          if (event.target.value === '') return onChange(undefined);
          const parsed = Number.parseInt(event.target.value, 10);
          if (Number.isFinite(parsed) && parsed >= 0) onChange(parsed);
        }}
      />
    </FieldShell>
  );
}

/** FHIR date primitive (YYYY-MM-DD); native date control. */
export function DateInput({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  required?: boolean;
}) {
  const invalid = Boolean(required && !value);
  return (
    <FieldShell label={label} required={required} invalid={invalid}>
      <Input
        type="date"
        value={value ?? ''}
        aria-invalid={invalid || undefined}
        onChange={(event) => onChange(event.target.value || undefined)}
      />
    </FieldShell>
  );
}

const NONE_VALUE = '__none__';

/**
 * Closed-value-set select for CodeableConcept fields. Selection is matched
 * by coding code; choosing an option writes a full coding + text concept.
 * Always offers a "None" item so an optional field can be cleared.
 */
export function ConceptSelectInput({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: CodeableConcept | undefined;
  onChange: (next: CodeableConcept | undefined) => void;
  options: ReadonlyArray<ConceptOption>;
  placeholder?: string;
}) {
  const selectedCode = value?.coding?.[0]?.code;
  const valueLabel = value?.text ?? value?.coding?.[0]?.display;
  // Code match first; display-text fallback covers concepts stored without
  // a coding (e.g. `{ text: 'Male' }`) so they read as real selections.
  const selected =
    options.find((option) => option.code === selectedCode) ??
    (valueLabel
      ? options.find(
          (option) =>
            option.display.toLowerCase() === valueLabel.trim().toLowerCase(),
        )
      : undefined);
  const offListLabel = selected ? undefined : valueLabel;
  return (
    <FieldShell label={label}>
      <Select
        value={selected?.code ?? ''}
        onValueChange={(next) => {
          if (next === NONE_VALUE) return onChange(undefined);
          const option = options.find((candidate) => candidate.code === next);
          onChange(option ? conceptFromOption(option) : undefined);
        }}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={
              offListLabel ? (
                // An off-list stored value is still a value — don't let the
                // trigger's placeholder styling gray it out.
                <span className="text-text-primary">{offListLabel}</span>
              ) : (
                (placeholder ?? 'Select…')
              )
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>None</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              {option.display}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}

/**
 * Multi-select over a roster of resources, producing FHIR `Reference[]`.
 *
 * Presentational per the data boundary: the roster is handed in (from the
 * feature lib, usually via FhirReferenceOptionsProvider), never fetched here.
 *
 * Each written reference carries a `display` snapshot alongside `reference`,
 * so a read variant can name the target without resolving it. References
 * whose target is not in the roster are preserved untouched — the roster may
 * be filtered or still loading, and dropping them would silently delete data.
 * An empty selection maps to undefined so the field diffs to a remove.
 */
export function ReferenceMultiSelectInput<T extends Resource>({
  label,
  value,
  onChange,
  options,
  emptyLabel = 'No options available',
  hint,
}: {
  label: string;
  value: Reference<T>[] | undefined;
  onChange: (next: Reference<T>[] | undefined) => void;
  options: readonly T[];
  emptyLabel?: string;
  hint?: string;
}) {
  const selected = value ?? [];
  // An id-less resource can't be referenced; offering it would write
  // `Type/undefined`.
  const offerable = options.filter((option) => option.id);

  // Add/remove in place rather than rebuilding from the roster: order is
  // preserved (so a toggle round-trip doesn't diff dirty) and selections whose
  // target isn't on the roster are untouched by construction.
  const toggle = (option: T, checked: boolean) => {
    const optionRef = referenceOf(option);
    const next = checked
      ? isSelected(selected, optionRef)
        ? selected
        : [...selected, optionRef]
      : selected.filter(
          (ref) => referenceTarget(ref) !== referenceTarget(optionRef),
        );
    onChange(next.length > 0 ? next : undefined);
  };

  return (
    <FieldShell label={label} hint={hint}>
      {offerable.length === 0 ? (
        <Text size="xs" color="tertiary">
          {emptyLabel}
        </Text>
      ) : (
        <Stack gap={2} className="pt-1">
          {offerable.map((option) => {
            const optionRef = referenceOf(option);
            const checked = isSelected(selected, optionRef);
            return (
              <Stack
                key={optionRef.reference}
                direction="horizontal"
                gap={2}
                align="center"
                fullWidth={false}
              >
                <Checkbox
                  checked={checked}
                  aria-label={optionRef.display}
                  onCheckedChange={(next) => toggle(option, next === true)}
                />
                <Text inline size="sm">
                  {optionRef.display}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      )}
    </FieldShell>
  );
}

function referenceOf<T extends Resource>(resource: T): Reference<T> {
  return {
    reference: `${resource.resourceType}/${resource.id}`,
    display: resourceDisplayName(resource),
  };
}

/**
 * The `Type/id` a reference points at, whatever form it was written in —
 * relative, absolute (`https://host/fhir/R4/Location/abc`), or version-pinned
 * (`Location/abc/_history/2`). Matching on the raw string instead would read a
 * legally-stored absolute reference as unselected, and checking its box would
 * then link the same target twice.
 */
function referenceTarget(reference: Reference): string | undefined {
  if (!reference.reference) return undefined;
  const [withoutHistory] = reference.reference.split('/_history/');
  const parts = (withoutHistory ?? '').split('/');
  if (parts.length < 2) return undefined;
  return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
}

function isSelected(selected: readonly Reference[], candidate: Reference) {
  const target = referenceTarget(candidate);
  return selected.some((ref) => referenceTarget(ref) === target);
}

/**
 * Closed-code select for required-binding FHIR code fields (e.g.
 * administrative gender). Options are the caller's — terminology-backed
 * value sets come later.
 */
export function CodeSelectInput<T extends string>({
  label,
  value,
  onChange,
  options,
  placeholder,
  hint,
}: {
  label: string;
  value: T | undefined;
  onChange: (next: T) => void;
  /** `description` renders as a subtext line under the option's label. */
  options: ReadonlyArray<{ value: T; label: string; description?: string }>;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <FieldShell label={label} hint={hint}>
      <Select value={value ?? ''} onValueChange={(next) => onChange(next as T)}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder ?? 'Select…'} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.description ? (
                <Stack gap={0} align="start">
                  <Text inline size="sm">
                    {option.label}
                  </Text>
                  <Text inline size="xs" color="tertiary">
                    {option.description}
                  </Text>
                </Stack>
              ) : (
                option.label
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}
